import { useUserLanguage, useCreateOwnerContractContext } from '@/customHooks';
import { ownerContractLeaseTypeMapper } from '@/helpers/ownerContractLeaseTypeMapper';
import { OwnerContractLeaseTypeOption, PaymentFrequencyOption } from '@/graphql/generated';
import textsData from '@/textData/CreateContractsTexts.json';

// Icons
import personIcon from '@/assets/contract-icons/person.svg'
import calendarIcon from '@/assets/contract-icons/calendar-icon.svg'
import rentIcon from '@/assets/contract-icons/montly-rent-icon.svg'
import termIcon from '@/assets/contract-icons/fixed-term-icon.svg'

interface SummaryItemProps {
  label: string;
  value: string | number | string[];
  icon?: string;
  isFullWidth?: boolean;
}

const SummaryItem = ({ label, value, icon, isFullWidth }: SummaryItemProps) => {
  return (
    <div className={`flex flex-col gap-1 p-3 rounded-lg bg-background2 ${isFullWidth ? 'col-span-2' : ''}`}>
      <div className="flex items-center gap-2">
        {icon && <img src={icon} alt="" className="w-4 h-4 opacity-60" />}
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-sm font-medium text-text-color">
        {Array.isArray(value) ? (
          <div className="flex flex-wrap gap-2 mt-1">
            {value.map((v, i) => (
              <span key={i} className="px-2 py-0.5 bg-white rounded border border-gray-100 text-xs">
                {v}
              </span>
            ))}
          </div>
        ) : (
          value
        )}
      </div>
    </div>
  )
}

const SectionTitle = ({ title }: { title: string }) => (
  <h3 className="text-md font-bold text-primary1 mb-4 border-b pb-2 flex items-center gap-2">
    <div className="w-1.5 h-6 bg-primary1 rounded-full" />
    {title}
  </h3>
)

export const SelectOwnerContractSummary = () => {
  const { state } = useCreateOwnerContractContext();
  const userLanguage = useUserLanguage();

  const rootTexts = userLanguage === 'es' ? textsData.es : textsData.en;
  const texts = rootTexts.ownerContract.summary;

  const formatDate = (dateString?: string | null | Date) => {
    if (!dateString) return '-';
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    return date.toLocaleDateString(userLanguage, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const frequencyLabels: Record<string, { en: string; es: string }> = {
    [PaymentFrequencyOption.Monthly]: { en: 'Monthly', es: 'Mensual' },
    [PaymentFrequencyOption.Quarterly]: { en: 'Quarterly', es: 'Trimestral' },
    [PaymentFrequencyOption.Semiannually]: { en: 'Semiannually', es: 'Semestral' },
    [PaymentFrequencyOption.Yearly]: { en: 'Yearly', es: 'Anual' },
  };

  const getFrequencyLabel = (option: PaymentFrequencyOption) => {
    return frequencyLabels[option]?.[userLanguage === 'es' ? 'es' : 'en'] ?? option;
  };

  const getLeaseTypeLabel = (type: OwnerContractLeaseTypeOption | null) => {
    if (!type) return '-';
    return ownerContractLeaseTypeMapper(type, userLanguage);
  };

  const currency = 'EUR';

  return (
    <div className="flex justify-center w-full py-4">
      <div className="bg-white rounded-2xl p-8 shadow-[0_10px_30px_rgba(0,0,0,0.1)] w-full max-w-[850px] border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">{texts.title}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          
          {/* Participants & Property */}
          <div className="flex flex-col gap-8">
            <div>
              <SectionTitle title={userLanguage === 'es' ? 'Participantes' : 'Participants'} />
              <div className="grid grid-cols-1 gap-3">
                <SummaryItem 
                  label={texts.rows.owner} 
                  value={state.ownerName || '-'} 
                  icon={personIcon}
                />
              </div>
            </div>

            <div>
              <SectionTitle title={userLanguage === 'es' ? 'Propiedad' : 'Property'} />
              <div className="grid grid-cols-1 gap-3">
                <SummaryItem 
                  label={texts.rows.property} 
                  value={state.propertyName || '-'} 
                />
              </div>
            </div>
          </div>

          {/* Terms & Plazos */}
          <div className="flex flex-col gap-8">
            <div>
              <SectionTitle title={userLanguage === 'es' ? 'Términos' : 'Terms'} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SummaryItem 
                  label={userLanguage === 'es' ? 'Tipo de arrendamiento' : 'Lease Type'} 
                  value={getLeaseTypeLabel(state.ownerContractLeaseTypeOption)} 
                  icon={termIcon}
                  isFullWidth
                />
                <SummaryItem 
                   label={userLanguage === 'es' ? 'Frecuencia de pago' : 'Payment Frequency'} 
                   value={getFrequencyLabel(state.paymentFrequencyOption)} 
                   icon={rentIcon}
                   isFullWidth
                />
                <SummaryItem 
                  label={userLanguage === 'es' ? 'Fecha de inicio' : 'Start Date'} 
                  value={formatDate(state.startDate)} 
                  icon={calendarIcon}
                />
                <SummaryItem 
                  label={userLanguage === 'es' ? 'Fecha de fin' : 'Finish Date'} 
                  value={formatDate(state.finishDate)} 
                  icon={calendarIcon}
                />
              </div>
            </div>

            <div>
              <SectionTitle title={userLanguage === 'es' ? 'Finanzas' : 'Financials'} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SummaryItem 
                  label={texts.rows.amount} 
                  value={`${state.contractAmount || '0'} ${currency}`} 
                  icon={rentIcon}
                />
                <SummaryItem 
                  label={texts.rows.insurance} 
                  value={state.insuranceId || '-'} 
                />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

